import { CreditStatus } from '@/features/credit/interfaces/credit.interface';
import { BadgeVariant } from '@/shared/components/badge/badge.component';
import { ApiService } from '@/core/services/api/api.service';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, shareReplay, tap, throwError } from 'rxjs';
import { Etape, UpdateEtapeDto } from '../interfaces/itineraire.interface';

/**
 * Étapes de l'itinéraire crédit — et, à partir d'elles, les libellés/variants
 * de statut affichés partout dans l'app (badges, listes, détails).
 *
 * `providedIn: 'root'` en fait un singleton : `chargerStatuts()` ne fait
 * l'appel réseau qu'une fois (partagé via `shareReplay`), tous les appelants
 * suivants — peu importe le composant — reçoivent le résultat déjà en cache.
 * Déclenché par `creditStatutsResolver` sur la route racine `credit`, pour
 * que `statuts` soit déjà rempli au premier rendu (pas de flash "Statut N").
 */
@Injectable({ providedIn: 'root' })
export class EtapeService {
  private readonly api = inject(ApiService);

  private readonly statutsSignal = signal<CreditStatus>({} as CreditStatus);
  /** Libellé + variant par code statut — lecture seule, à jour dès le premier chargement. */
  readonly statuts = this.statutsSignal.asReadonly();

  private chargement$: Observable<Etape[]> | null = null;

  /** Charge les étapes et remplit `statuts` — une seule requête réseau, quel que soit le nombre d'appels. */
  chargerStatuts(): Observable<Etape[]> {
    if (!this.chargement$) {
      this.chargement$ = this.getEtapes().pipe(
        tap((etapes) => {
          this.statutsSignal.set(
            etapes.reduce(
              (acc, e) => ({
                ...acc,
                [e.statutCode]: { label: e.libelle, variant: this.variant(e.statutCode) },
              }),
              {} as CreditStatus,
            ),
          );
        }),
        catchError((err) => {
          // Échec : on efface le cache pour qu'une prochaine navigation retente l'appel.
          this.chargement$ = null;
          return throwError(() => err);
        }),
        shareReplay(1),
      );
    }
    return this.chargement$;
  }

  getEtapes() {
    return this.api.get<{ data: Etape[] }>('/credit/ListeEtape').pipe(
      map((res) => res.data ?? []),
      catchError((err) => throwError(() => err)),
    );
  }

  updateEtape(id: number, data: UpdateEtapeDto) {
    return this.api
      .put(`/credit/modifierEtape/${id}`, data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** Libellé du statut, à défaut son code brut. */
  label(statut: number): string {
    return this.statutsSignal()[statut]?.label ?? `Statut ${statut}`;
  }

  /** Variant de badge pour un statut : clôturé en succès, rejeté/annulé en erreur, le reste en attente. */
  variant(statut: number): BadgeVariant {
    switch (statut) {
      case 30:
        return 'success';
      case 21:
      case 24:
      case 25:
        return 'destructive';
      default:
        return 'warning';
    }
  }
}
